export interface CheckboxColors {
  selectedBackground?: string | null;
  selectedBorder?: string | null;
}

export function buildCheckboxCssRules(
  s: string,
  colors: CheckboxColors | null | undefined,
): string[] {
  if (!colors) {
    return [];
  }

  const bg = colors.selectedBackground || undefined;
  const border = colors.selectedBorder || undefined;

  if (!bg && !border) {
    return [];
  }

  const selectors = [
    `${s}[data-ui="item-checkbox"]`,
    `${s}[data-ui="bundle-group-checkbox"]`,
    // Selection-card-like radio card surfaces reuse the same tokens for their
    // selected bubble state.
    `${s}[data-ui="option"]`,
    `${s}[data-ui="order-selection-option"]`,
    `${s}[data-selection-card]`,
  ];
  const selectorList = selectors.join(", ");

  // bg / hover tokens are safe to set on the whole card subtree — they only
  // affect the radio bubble's fill and the card's hover/selected ring.
  const cardLevelVars: string[] = [];
  if (bg) {
    cardLevelVars.push(`--redo-colors-background-bg-alternate: ${bg};`);
    cardLevelVars.push(`--redo-colors-background-bg-secondary-solid: ${bg};`);
    cardLevelVars.push(`--redo-colors-text-text-secondary_hover: ${bg};`);
  }
  if (border) {
    // The border color also controls the checkmark icon color (renders inside
    // the checked bubble).
    cardLevelVars.push(`--redo-colors-foreground-fg-on-brand: ${border};`);
  }

  const rules: string[] = [];
  if (cardLevelVars.length > 0) {
    rules.push(`${selectorList} { ${cardLevelVars.join(" ")} }`);
  }

  if (border) {
    // The checkbox/radio bubble itself reads `--redo-colors-border-border-primary`
    // for its hairline border. Setting that token on the whole card leaks the
    // selected color to any other consumer inside the card — most visibly,
    // the <Divider/> on the shipment-method dropoff cards. Scope the override
    // to the actual input bubble so the divider keeps its neutral color.
    //
    // `_box_*` is the compiled CSS-modules class for the checkbox/radio
    // bubble in `redo-checkbox.module.css`, but matching it by hash would be
    // fragile. Instead, target the parent slot via [data-ui="item-checkbox"]
    // / [data-ui="bundle-group-checkbox"] / [data-ui="option"] / selection
    // cards and reach the inner div via descendant selectors.
    const bubbleSelectors = selectors
      .map(
        (sel) =>
          `${sel} input[type="checkbox"] + div, ${sel} input[type="radio"] + div`,
      )
      .join(", ");
    rules.push(
      `${bubbleSelectors} { --redo-colors-border-border-primary: ${border}; }`,
    );
  }

  // Quantity picker (cart Item) — borrow the selection styling so the
  // bordered input frame matches the rest of the merchant's themed surfaces.
  // The picker reads bg-primary + border-primary tokens scoped to itself.
  const quantityProps: string[] = [];
  if (bg) {
    quantityProps.push(`--redo-colors-background-bg-primary: ${bg};`);
  }
  if (border) {
    quantityProps.push(`--redo-colors-border-border-primary: ${border};`);
  }
  if (quantityProps.length > 0) {
    rules.push(
      `${s}[data-ui="quantity-picker"] { ${quantityProps.join(" ")} }`,
    );
  }

  return rules;
}
